import { Component, HostListener, Input, OnInit, Type } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FeatureFlagService } from 'src/app/services/feature-flags/feature-flag.service';
import { DatetimeSelectorComponent } from '../datetime-selector/datetime-selector.component';

/// Second-generation date/time picker. Same modal contract as
/// DatetimeSelectorComponent - inputs `minimumDate`/`inputDate`, dismisses with
/// the chosen Date or null - so a caller can swap one for the other without
/// touching its onDidDismiss handler.
///
/// What it adds over v1:
///   - quarter-hour presets (00/15/30/45) instead of 00/30;
///   - the minutes field is tappable and opens a keypad, so any minute 00-59
///     can be entered. Like the hours calculator, the value is built from the
///     LAST TWO digits typed and is clamped to 59.
///
/// Gated on the TIME_PICKER_V2 flag - use `DatetimeSelectorV2Component.pick()`
/// when opening the modal rather than checking the flag at each call site.
@Component({
  selector: 'app-datetime-selector-v2',
  templateUrl: './datetime-selector-v2.component.html',
  styleUrls: ['./datetime-selector-v2.component.scss'],
})
export class DatetimeSelectorV2Component implements OnInit {

  public static readonly FLAG = 'TIME_PICKER_V2';

  /// The picker component to open: v2 with the flag on, the original otherwise.
  public static pick(featureFlags: FeatureFlagService): Type<any> {
    return featureFlags.isEnabled(DatetimeSelectorV2Component.FLAG)
      ? DatetimeSelectorV2Component
      : DatetimeSelectorComponent;
  }

  @Input() minimumDate: Date;
  @Input() inputDate: Date;

  selectedDate: Date;

  readonly hourOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  readonly minuteOptions = [0, 15, 30, 45];

  hour: number;        // 1-12
  minute: number;      // 0-59
  isPm: boolean;
  canGoPrev = true;

  // Minute keypad. `fresh` means the field is showing the old value selected -
  // the next digit replaces it rather than appending to it.
  editing = false;
  entry = '';
  fresh = true;

  readonly keypad = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'back', '0', 'done'];

  private originalDate: Date;

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    this.originalDate = this.isValid(this.inputDate) ? new Date(this.inputDate) : new Date();
    this.selectedDate = new Date(this.originalDate);
    this.updateState();
  }

  // ---- date -------------------------------------------------------------

  prevDay() {
    this.shiftDay(-1);
  }

  nextDay() {
    this.shiftDay(1);
  }

  private shiftDay(days: number) {
    const next = this.addDays(this.selectedDate, days);
    if (this.isBeforeMinimum(next)) {
      return;
    }
    this.selectedDate = next;
    this.updateState();
  }

  // ---- time -------------------------------------------------------------

  setHour(hour: number) {
    this.applyTime(hour, this.minute, this.isPm);
  }

  setMinute(minute: number) {
    this.stopEditing();
    this.applyTime(this.hour, minute, this.isPm);
  }

  setAmPm(pm: boolean) {
    this.applyTime(this.hour, this.minute, pm);
  }

  // Every edit goes through here, so 12 AM is midnight and 12 PM is noon and
  // changing the hour never rolls the date into the next day.
  private applyTime(hour: number, minute: number, pm: boolean) {
    const date = new Date(this.selectedDate);
    date.setHours((hour % 12) + (pm ? 12 : 0), minute, 0, 0);
    this.selectedDate = date;
    this.updateState();
  }

  clear() {
    this.stopEditing();
    this.selectedDate = new Date(this.originalDate);
    this.updateState();
  }

  // ---- minute keypad ----------------------------------------------------

  openMinuteEditor() {
    this.editing = true;
    this.entry = this.pad(this.minute);
    this.fresh = true;
  }

  pressKey(key: string) {
    if (key === 'done') {
      this.stopEditing();
    } else if (key === 'back') {
      this.backspace();
    } else {
      this.pressDigit(key);
    }
  }

  private pressDigit(digit: string) {
    this.entry = this.fresh ? digit : (this.entry + digit).slice(-2);
    this.fresh = false;
    this.applyTime(this.hour, Math.min(59, parseInt(this.entry, 10) || 0), this.isPm);
  }

  private backspace() {
    this.entry = this.fresh ? '' : this.entry.slice(0, -1);
    this.fresh = false;
    this.applyTime(this.hour, parseInt(this.entry, 10) || 0, this.isPm);
  }

  private stopEditing() {
    this.editing = false;
    this.entry = '';
    this.fresh = true;
  }

  // A hardware keyboard (desktop, tablet with keyboard) drives the same keypad.
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.editing) {
      return;
    }
    if (/^[0-9]$/.test(event.key)) {
      this.pressDigit(event.key);
    } else if (event.key === 'Backspace') {
      this.backspace();
    } else if (event.key === 'Enter' || event.key === 'Escape') {
      this.stopEditing();
    } else {
      return;
    }
    event.preventDefault();
  }

  get minuteLabel(): string {
    if (this.editing && !this.fresh) {
      return this.entry === '' ? '' : this.entry.padStart(2, '0');
    }
    return this.pad(this.minute);
  }

  // ---- modal ------------------------------------------------------------

  confirm() {
    this.modalController.dismiss(this.selectedDate);
  }

  cancel() {
    this.modalController.dismiss(null);
  }

  // ---- helpers ----------------------------------------------------------

  private updateState() {
    const h24 = this.selectedDate.getHours();
    this.hour = h24 % 12 === 0 ? 12 : h24 % 12;
    this.minute = this.selectedDate.getMinutes();
    this.isPm = h24 >= 12;
    this.canGoPrev = !this.isBeforeMinimum(this.addDays(this.selectedDate, -1));
  }

  private isBeforeMinimum(date: Date): boolean {
    return this.isValid(this.minimumDate) && date < this.minimumDate;
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  private pad(n: number): string {
    return String(n ?? 0).padStart(2, '0');
  }
}
